/**
 * UserRooftopAccessModal
 *
 * Master Admin modal for granting a dealer-side user access to multiple rooftops
 * within their Dealer Group. Only meaningful when the dealer (business) is in a
 * Dealer Group; otherwise the host should disable the action entirely.
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Skeleton } from "../ui/skeleton";

export const UserRooftopAccessModal = ({ open, onClose, user, dealerGroupId }) => {
  const { apiFetch } = useAuth();
  const { success: toastOk, error: toastErr } = useToast();

  const [loading, setLoading] = useState(false);
  const [groupRooftops, setGroupRooftops] = useState([]);
  const [primaryBusinessId, setPrimaryBusinessId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    if (!user || !dealerGroupId) return;
    setLoading(true);
    try {
      // Fetch group rooftops + user's existing access in parallel
      const [groupRes, userRes] = await Promise.all([
        apiFetch(`/admin/dealer-groups/${dealerGroupId}`, { method: "GET" }),
        apiFetch(`/admin/users/${user.id}/rooftops`, { method: "GET" }),
      ]);
      setGroupRooftops(groupRes.data?.rooftops || []);
      setPrimaryBusinessId(userRes.data?.primary_business_id || null);
      const granted = new Set(
        (userRes.data?.rooftops || []).map((r) => r.id)
      );
      setSelectedIds(granted);
    } catch (e) {
      toastErr(e?.response?.data?.detail || "Failed to load rooftop access");
    } finally {
      setLoading(false);
    }
  }, [apiFetch, user, dealerGroupId, toastErr]);

  useEffect(() => {
    if (open) reload();
  }, [open, reload]);

  const toggle = (id) => {
    if (id === primaryBusinessId) return; // primary is implicit, can't toggle off
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Persist the selected set; primary is implicit so no need to include
      const business_ids = Array.from(selectedIds).filter((id) => id !== primaryBusinessId);
      await apiFetch(`/admin/users/${user.id}/rooftops`, {
        method: "PUT",
        data: { business_ids },
      });
      toastOk("Rooftop access updated");
      onClose();
    } catch (e) {
      toastErr(e?.response?.data?.detail || "Failed to update rooftop access");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="user-rooftop-access-modal">
        <DialogHeader>
          <DialogTitle>Rooftop Access</DialogTitle>
          <DialogDescription>
            Select which rooftops <strong>{user?.email}</strong> can access. They will be able to
            switch between selected rooftops via the top-nav switcher.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          {loading ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => <Skeleton key={i} className="h-9 w-full" />)}
            </div>
          ) : groupRooftops.length === 0 ? (
            <p className="text-sm text-gray-500">This dealer group has no rooftops yet.</p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {groupRooftops.map((r) => {
                const isPrimary = r.id === primaryBusinessId;
                const checked = selectedIds.has(r.id);
                return (
                  <label
                    key={r.id}
                    className={`flex items-center gap-3 p-2 rounded border ${
                      checked ? "bg-purple-50 border-purple-200" : "border-gray-200"
                    } ${isPrimary ? "opacity-80" : "cursor-pointer hover:bg-gray-50"}`}
                    data-testid={`rooftop-access-row-${r.id}`}
                  >
                    <Checkbox
                      checked={checked || isPrimary}
                      disabled={isPrimary}
                      onCheckedChange={() => toggle(r.id)}
                      data-testid={`rooftop-access-checkbox-${r.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                        {r.name}
                        {isPrimary && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">
                            Primary
                          </span>
                        )}
                        {r.franchise_make && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                            {r.franchise_make}
                          </span>
                        )}
                      </div>
                      {isPrimary && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          User&apos;s home rooftop — always accessible.
                        </p>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving || loading}
            data-testid="save-rooftop-access-btn"
          >
            {saving ? "Saving…" : "Save Access"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UserRooftopAccessModal;
